import { useTranslation } from 'react-i18next'
import { usePan } from '@embedpdf/plugin-pan/react'
import { useCapture } from '@embedpdf/plugin-capture/react'
import { useAnnotation } from '@embedpdf/plugin-annotation/react'
import { useExport } from '@embedpdf/plugin-export/react'
import { usePrint } from '@embedpdf/plugin-print/react'
import { useDocumentManagerCapability } from '@embedpdf/plugin-document-manager/react'
import { ViewerModeTabs, type ViewerMode } from './ViewerModeTabs'
import { type RightPanelMode } from './SimplePdfViewer'
import { setFontLanguage } from '../../utils/fontLanguage'
import { areaOcrDeactivateRef, unsavedChangesRef } from './ContextualToolbar'
import { CollapsedMenu } from './CollapsedMenu'
import { useResponsive } from '../../hooks/useResponsive'
import { saveDocument } from '../../services/indexeddb-service'
import { writeToFile } from '../../services/file-handle-service'
import { setPreference, getPreference } from '../../services/indexeddb-service'
import { Tooltip } from '../Tooltip'
import { Button } from '../ui/button'
import { Icon } from '../ui/icon'
import { useState, useCallback, useRef, useEffect } from 'react'
import { SettingsModal } from './SettingsModal'
import { HelpModal } from './HelpModal'
import { InfoModal } from './InfoModal'

interface PrimaryToolbarProps {
  documentId: string
  activeMode: ViewerMode
  onModeChange: (mode: ViewerMode) => void
  isSidebarOpen: boolean
  onSidebarToggle: () => void
  isRightPanelOpen: boolean
  rightPanelMode: RightPanelMode
  setRightPanelMode: (mode: RightPanelMode) => void
  setIsRightPanelOpen: (value: boolean | ((prev: boolean) => boolean)) => void
}

export const PrimaryToolbar = ({ 
  documentId, 
  activeMode, 
  onModeChange, 
  isSidebarOpen, 
  onSidebarToggle,
  isRightPanelOpen,
  rightPanelMode,
  setRightPanelMode,
  setIsRightPanelOpen,
}: PrimaryToolbarProps) => {
  const { t, i18n } = useTranslation()
  const { provides: panApi, isPanning } = usePan(documentId)
  const { provides: captureApi, state: captureState } = useCapture(documentId)
  const { provides: annotationApi, state: annotationState } = useAnnotation(documentId)
  const { provides: exportApi } = useExport(documentId)
  const { provides: printApi } = usePrint(documentId)
  const { provides: docManager } = useDocumentManagerCapability()

  const isCompact = useResponsive()
  const [menuOpen, setMenuOpen] = useState(false)
  const [showCloseModal, setShowCloseModal] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const isMarqueeActive = captureState?.isMarqueeCaptureActive ?? false
  const activeToolId = annotationState?.activeToolId ?? null
  const defaultPanSet = useRef(false)

  useEffect(() => {
    if (isCompact && !isPanning && !defaultPanSet.current && panApi && documentId) {
      panApi.togglePan()
      defaultPanSet.current = true
    }
  }, [isCompact, isPanning, panApi, documentId])
  const isSelectionActive = !isPanning && !isMarqueeActive && !activeToolId

  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  useEffect(() => {
    const handler = () => setShowSettings(true)
    window.addEventListener('opakor-open-settings', handler)
    return () => window.removeEventListener('opakor-open-settings', handler)
  }, [])

  const handleSelection = () => {
    if (isPanning && panApi) {
      panApi.togglePan()
    }
    if (isMarqueeActive && captureApi) {
      captureApi.toggleMarqueeCapture()
      areaOcrDeactivateRef.current?.()
    }
    if (activeToolId && annotationApi) {
      annotationApi.setActiveTool(null)
    }
  }

  const handlePan = () => {
    if (isMarqueeActive && captureApi) {
      captureApi.toggleMarqueeCapture()
      areaOcrDeactivateRef.current?.()
    }
    if (activeToolId && annotationApi) {
      annotationApi.setActiveTool(null)
    }
    panApi?.togglePan()
  }

  const handleSave = useCallback(() => {
    const task = exportApi?.saveAsCopy()
    if (!task) return
    const docMeta = docManager?.getDocumentState(documentId)
    const docName = docMeta?.name || docMeta?.id || 'annotated-document.pdf'
    task.wait(async (buffer) => {
      writeToFile(buffer).catch(() => {})
      const docRecord = {
        id: docName,
        name: docName,
        buffer,
        byteLength: buffer.byteLength,
        currentPage: 0,
        lastOpened: Date.now(),
      }
      saveDocument(docRecord).catch(() => {})
      saveDocument({ ...docRecord, id: 'current-document' }).catch(() => {})
      unsavedChangesRef.current = false
    }, () => {})
    setMenuOpen(false)
  }, [exportApi, docManager, documentId])

  const handleSaveAs = useCallback(async () => {
    setMenuOpen(false)
    const task = exportApi?.saveAsCopy()
    if (!task) return
    task.wait(async (buffer) => {
      let chosenName = 'document.pdf'
      let cancelled = false
      try {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: docManager?.getDocumentState(documentId)?.name || 'document.pdf',
          types: [{ description: 'PDF Files', accept: { 'application/pdf': ['.pdf'] } }],
        })
        chosenName = handle.name
        const writable = await handle.createWritable()
        await writable.write(buffer)
        await writable.close()
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          cancelled = true
        } else {
          const name = docManager?.getDocumentState(documentId)?.name || 'document.pdf'
          chosenName = name
          const downloadName = name.endsWith('.pdf') ? name : `${name}.pdf`
          const blob = new Blob([buffer], { type: 'application/pdf' })
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = downloadName
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        }
      }
      if (cancelled) return
      const meta = docManager?.getDocumentState(documentId)
      if (meta?.name) {
        await getPreference<string[]>('open-documents').then(list =>
          setPreference('open-documents', (list || []).filter(id => id !== meta.name).concat(chosenName))
        )
      }
      docManager?.closeDocument(documentId)
      docManager?.openDocumentBuffer({ name: chosenName, buffer, autoActivate: true })
      saveDocument({
        id: chosenName,
        name: chosenName,
        buffer,
        byteLength: buffer.byteLength,
        currentPage: 0,
        lastOpened: Date.now(),
      }).catch(() => {})
      saveDocument({
        id: 'current-document',
        name: chosenName,
        buffer,
        byteLength: buffer.byteLength,
        currentPage: 0,
        lastOpened: Date.now(),
      }).catch(() => {})
    }, () => {})
  }, [exportApi, docManager, documentId])

  const handleShare = useCallback(async () => {
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({ url })
      } catch {}
      setMenuOpen(false)
      return
    }
    try {
      await navigator.clipboard.writeText(url)
    } catch {}
    setMenuOpen(false)
  }, [])

  const handlePrint = useCallback(() => {
    if (!printApi) return
    printApi.print({ includeAnnotations: true })
    setMenuOpen(false)
  }, [printApi])

  const handleOpenFile = useCallback(() => {
    document.getElementById('tab-bar-file-input')?.click()
    setMenuOpen(false)
  }, [])

  const handleCloseFile = useCallback(async () => {
    setMenuOpen(false)
    console.log('[PrimaryToolbar] ========== handleCloseFile called for documentId:', documentId)
    if (unsavedChangesRef.current) {
      setShowCloseModal(true)
    } else {
      const meta = docManager?.getDocumentState(documentId)
      console.log('[PrimaryToolbar] Closing document, meta:', meta)
      if (meta?.name) {
        console.log('[PrimaryToolbar] Removing from open-documents:', meta.name)
        await getPreference<string[]>('open-documents').then(list => {
          console.log('[PrimaryToolbar] Current open-documents:', list)
          const newList = (list || []).filter(id => id !== meta.name)
          console.log('[PrimaryToolbar] New open-documents:', newList)
          return setPreference('open-documents', newList)
        })
      }
      docManager?.closeDocument(documentId)
      console.log('[PrimaryToolbar] Document closed from UI')
      // Check if this was the last document
      const allDocs = docManager?.getOpenDocuments?.()
      console.log('[PrimaryToolbar] Remaining open documents after close:', allDocs?.length || 0)
      if (allDocs && allDocs.length === 0) {
        console.log('[PrimaryToolbar] This was the last document - clearing current-document preference')
        // Should clear current-document here
      }
    }
  }, [docManager, documentId])

  const handleSaveAndClose = useCallback(async () => {
    if (!exportApi) return
    setShowCloseModal(false)
    const task = exportApi.saveAsCopy()
    if (!task) return
    task.wait(async (buffer: ArrayBuffer) => {
      const docMeta = docManager?.getDocumentState(documentId)
      const docName = docMeta?.name || docMeta?.id || 'annotated-document.pdf'
      const docRecord = {
        id: docName,
        name: docName,
        buffer,
        byteLength: buffer.byteLength,
        currentPage: 0,
        lastOpened: Date.now(),
      }
      try {
        await writeToFile(buffer)
        await saveDocument({ ...docRecord, id: 'current-document' })
        await saveDocument(docRecord)
      } catch {
        await saveDocument({ ...docRecord, id: 'current-document' })
        await saveDocument(docRecord)
      }
      unsavedChangesRef.current = false
      if (docName) {
        await getPreference<string[]>('open-documents').then(list =>
          setPreference('open-documents', (list || []).filter(id => id !== docName))
        )
      }
      docManager?.closeDocument(documentId)
    }, () => {
      docManager?.closeDocument(documentId)
    })
  }, [exportApi, docManager, documentId])

  const handleCloseWithoutSaving = useCallback(async () => {
    setShowCloseModal(false)
    console.log('[PrimaryToolbar] ========== handleCloseWithoutSaving called for documentId:', documentId)
    unsavedChangesRef.current = false
    const meta = docManager?.getDocumentState(documentId)
    console.log('[PrimaryToolbar] Closing without saving, meta:', meta)
    if (meta?.name) {
      console.log('[PrimaryToolbar] Removing from open-documents:', meta.name)
      await getPreference<string[]>('open-documents').then(list => {
        console.log('[PrimaryToolbar] Current open-documents:', list)
        const newList = (list || []).filter(id => id !== meta.name)
        console.log('[PrimaryToolbar] New open-documents:', newList)
        return setPreference('open-documents', newList)
      })
    }
    docManager?.closeDocument(documentId)
    console.log('[PrimaryToolbar] Document closed without saving from UI')
    // Check if this was the last document
    const allDocs = docManager?.getOpenDocuments?.()
    console.log('[PrimaryToolbar] Remaining open documents after close:', allDocs?.length || 0)
    if (allDocs && allDocs.length === 0) {
      console.log('[PrimaryToolbar] This was the last document - clearing current-document preference')
      // Should clear current-document here
    }
  }, [docManager, documentId])

  const handleCancelClose = useCallback(() => {
    setShowCloseModal(false)
  }, [])

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng)
    setFontLanguage(lng as 'en' | 'km')
  }

  const menuItemGroups = [
    {
      id: 'file',
      items: [
        { id: 'open', icon: 'fileImport', label: t('tabBar.openFile') || 'Open File...', onClick: handleOpenFile },
        { id: 'close', icon: 'x', label: t('tooltips.closeDocument') || 'Close', onClick: handleCloseFile },
      ],
    },
    {
      id: 'actions',
      items: [
        { id: 'save', icon: 'save', label: t('tooltips.save') || 'Save', onClick: handleSave },
        { id: 'saveAs', icon: 'saveAll', label: t('tooltips.saveAs') || 'Save As', onClick: handleSaveAs },
        { id: 'share', icon: 'externalLink', label: t('tooltips.share') || 'Share', onClick: handleShare },
        { id: 'print', icon: 'print', label: t('tooltips.print') || 'Print', onClick: handlePrint },
      ],
    },
    {
      id: 'more',
      items: [
        { id: 'settings', icon: 'settings', label: t('settings.title') || 'Settings', onClick: () => { setMenuOpen(false); setShowSettings(true) } },
        { id: 'help', icon: 'info', label: t('help.title') || 'Help', onClick: () => { setMenuOpen(false); setShowHelp(true) } },
        { id: 'about', icon: 'book', label: t('info.title') || 'About', onClick: () => { setMenuOpen(false); setShowInfo(true) } },
      ],
    },
  ]

  return (
    <>
    <div className="h-14 bg-bg-surface-alt flex items-center justify-between px-2 border-b-[0.5px] border-border-default/50 shadow-sm relative z-20">
      <div className="flex items-center gap-2">
        <div className="relative" ref={menuRef}>
          <Tooltip content={t('tooltips.menu')} position="bottom">
            <Button
              onClick={() => setMenuOpen(!menuOpen)}
              active={menuOpen}
              className={menuOpen ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="menu" size={20} />
            </Button>
          </Tooltip>
          {menuOpen && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-bg-surface border border-border-default rounded-xl shadow-2xl py-1 z-50">
              {menuItemGroups.map((group, gi) => (
                <div key={group.id}>
                  {gi > 0 && <div className="h-px bg-border-default my-1" />}
                  {group.items.map((item) => (
                    <button
                      key={item.id}
                      onClick={item.onClick}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-fg-primary hover:bg-accent/10 hover:text-accent transition-colors"
                    >
                      <Icon icon={item.icon} size={16} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              ))}
          </div>
        )}
        </div>

        <div className="h-6 w-px bg-border-default" />

        <Tooltip content={t('tooltips.sidebar')} position="bottom">
          <Button
            onClick={onSidebarToggle}
            active={isSidebarOpen}
            className={isSidebarOpen ? 'bg-accent/10 text-accent ring-1 ring-accent shadow-[0_0_10px_rgba(59,130,246,0.3)]' : 'text-fg-secondary hover:text-fg-primary'}
          >
            <Icon icon="sidebar" size={20} />
          </Button>
        </Tooltip>

        <div className="h-6 w-px bg-border-default" />

        {isCompact ? (
          <CollapsedMenu>
            <div className="flex flex-col gap-3 min-w-[200px]">
              <div className="flex items-center gap-1">
                <Tooltip content={t('tooltips.selectionTool') || 'Selection Tool'} position="bottom">
                  <Button
                    onClick={handleSelection}
                    active={isSelectionActive}
                    className={isSelectionActive ? '' : 'text-fg-secondary hover:text-fg-primary'}
                  >
                    <Icon icon="pointer" size={20} />
                  </Button>
                </Tooltip>
                <Tooltip content={t('tooltips.handTool') || 'Hand Tool'} position="bottom">
                  <Button
                    onClick={handlePan}
                    active={isPanning}
                    className={isPanning ? '' : 'text-fg-secondary hover:text-fg-primary'}
                  >
                    <Icon icon="hand" size={20} />
                  </Button>
                </Tooltip>
              </div>

              <div className="w-full h-px bg-border-default" />

              <div className="flex items-center gap-1">
                <Tooltip content={t('tooltips.save') || 'Save'} position="bottom">
                  <Button onClick={handleSave} className="text-fg-secondary hover:text-fg-primary">
                    <Icon icon="save" size={20} />
                  </Button>
                </Tooltip>
                <Tooltip content={t('tooltips.saveAs') || 'Save As'} position="bottom">
                  <Button onClick={handleSaveAs} className="text-fg-secondary hover:text-fg-primary">
                    <Icon icon="saveAll" size={20} />
                  </Button>
                </Tooltip>
              </div>

              <div className="w-full h-px bg-border-default" />

              <div className="flex items-center rounded-full border border-border-default bg-bg-app p-1">
                <button
                  onClick={() => changeLanguage('km')}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${i18n.language.startsWith('km') ? 'bg-bg-surface text-fg-primary' : 'text-fg-secondary hover:text-fg-primary'}`}
                >
                  ភាសាខ្មែរ
                </button>
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${i18n.language.startsWith('en') ? 'bg-bg-surface text-fg-primary' : 'text-fg-secondary hover:text-fg-primary'}`}
                >
                  English
                </button>
              </div>
            </div>
          </CollapsedMenu>
        ) : (
          <div className="flex items-center gap-1">
            <Tooltip content={t('tooltips.selectionTool') || 'Selection Tool'} position="bottom">
              <Button
                onClick={handleSelection}
                active={isSelectionActive}
                className={isSelectionActive ? '' : 'text-fg-secondary hover:text-fg-primary'}
              >
                <Icon icon="pointer" size={20} />
              </Button>
            </Tooltip>
            <Tooltip content={t('tooltips.handTool') || 'Hand Tool'} position="bottom">
              <Button
                onClick={handlePan}
                active={isPanning}
                className={isPanning ? '' : 'text-fg-secondary hover:text-fg-primary'}
              >
                <Icon icon="hand" size={20} />
              </Button>
            </Tooltip>
            <div className="h-6 w-px bg-border-default" />
            <Tooltip content={t('tooltips.save') || 'Save'} position="bottom">
              <Button onClick={handleSave} className="text-fg-secondary hover:text-fg-primary">
                <Icon icon="save" size={20} />
              </Button>
            </Tooltip>
            <Tooltip content={t('tooltips.saveAs') || 'Save As'} position="bottom">
              <Button onClick={handleSaveAs} className="text-fg-secondary hover:text-fg-primary">
                <Icon icon="saveAll" size={20} />
              </Button>
            </Tooltip>
          </div>
        )}
      </div>

      <div className={`flex-1 flex justify-center ${isCompact ? 'overflow-x-auto mx-2' : ''}`}>
        <ViewerModeTabs
          activeMode={activeMode}
          onModeChange={onModeChange}
        />
      </div>

      <div className="flex items-center gap-2">
        <Tooltip content={t('tooltips.search')} position="bottom">
          <Button 
            onClick={() => {
              if (isRightPanelOpen && rightPanelMode === 'search') {
                setIsRightPanelOpen(false)
              } else {
                setRightPanelMode('search')
                setIsRightPanelOpen(true)
              }
            }}
            active={isRightPanelOpen && rightPanelMode === 'search'}
            className={isRightPanelOpen && rightPanelMode === 'search' ? 'bg-accent/10 text-accent ring-1 ring-accent shadow-[0_0_10px_rgba(59,130,246,0.3)]' : 'text-fg-secondary hover:text-fg-primary'}
          >
            <Icon icon="search" size={20} />
          </Button>
        </Tooltip>

        <Tooltip content={t('tooltips.messages')} position="bottom">
          <Button 
            onClick={() => {
              if (isRightPanelOpen && rightPanelMode === 'comments') {
                setIsRightPanelOpen(false)
              } else {
                setRightPanelMode('comments')
                setIsRightPanelOpen(true)
              }
            }}
            active={isRightPanelOpen && rightPanelMode === 'comments'}
            className={isRightPanelOpen && rightPanelMode === 'comments' ? 'bg-accent/10 text-accent ring-1 ring-accent shadow-[0_0_10px_rgba(59,130,246,0.3)]' : 'text-fg-secondary hover:text-fg-primary'}
          >
            <Icon icon="comment" size={20} />
          </Button>
        </Tooltip>

        {!isCompact && (
          <div className="flex items-center rounded-full border border-border-default bg-bg-app p-1">
            <button
              onClick={() => changeLanguage('km')}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${i18n.language.startsWith('km') ? 'bg-bg-surface text-fg-primary shadow-[0_0_10px_rgba(59,130,246,0.15)]' : 'text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-alt'}`}
            >
              ភាសាខ្មែរ
            </button>
            <button
              onClick={() => changeLanguage('en')}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${i18n.language.startsWith('en') ? 'bg-bg-surface text-fg-primary shadow-[0_0_10px_rgba(59,130,246,0.15)]' : 'text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-alt'}`}
            >
              English
            </button>
          </div>
        )}
      </div>
    </div>
    {showCloseModal && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 animate-in fade-in duration-200">
        <div className="bg-bg-surface rounded-2xl shadow-2xl border border-border-default p-6 max-w-sm w-full mx-4 animate-in zoom-in-95 duration-200">
          <h3 className="text-lg font-semibold text-fg-primary mb-2">{t('app.unsavedChangesTitle') || 'Unsaved Changes'}</h3>
          <p className="text-sm text-fg-muted mb-6">{t('app.unsavedChangesHint') || 'You have unsaved changes. What would you like to do?'}</p>
          <div className="flex flex-col gap-2">
            <button
              onClick={handleSaveAndClose}
              className="w-full px-4 py-2.5 bg-accent text-white rounded-xl text-sm font-medium hover:bg-accent/90 transition-colors"
            >
              {t('app.saveAndClose') || 'Save and Close'}
            </button>
            <button
              onClick={handleCloseWithoutSaving}
              className="w-full px-4 py-2.5 bg-bg-surface-alt text-fg-primary rounded-xl text-sm font-medium hover:bg-interactive-hover transition-colors border border-border-default"
            >
              {t('app.closeWithoutSaving') || 'Close without Saving'}
            </button>
            <button
              onClick={handleCancelClose}
              className="w-full px-4 py-2.5 text-fg-muted rounded-xl text-sm hover:text-fg-primary hover:bg-interactive-hover transition-colors"
            >
              {t('app.cancel') || 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    )}
    <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />
    <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
    <InfoModal isOpen={showInfo} onClose={() => setShowInfo(false)} />
    </>
  )
}
